/**
 * Única fuente de verdad para interpretar `scheduledDelivery` de un pedido.
 * Los componentes deben consumir el objeto normalizado; no leer enabled/date/start/end.
 *
 * reservationId se ignora a propósito (solo procesos internos).
 */

const TIME_PATTERN = /^([01]\d|2[0-3]):[0-5]\d$/
const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/

const MONTH_SHORT_ES = [
  'ene',
  'feb',
  'mar',
  'abr',
  'may',
  'jun',
  'jul',
  'ago',
  'sep',
  'oct',
  'nov',
  'dic',
]

/**
 * @param {unknown} value
 * @returns {string}
 */
function asTrimmedString(value) {
  if (value === null || value === undefined) return ''
  return String(value).trim()
}

/**
 * @param {string} dateStr - YYYY-MM-DD
 * @returns {{ day: number, monthIndex: number, year: number }|null}
 */
function parseIsoDateParts(dateStr) {
  if (!DATE_PATTERN.test(dateStr)) return null
  const [yearStr, monthStr, dayStr] = dateStr.split('-')
  const year = Number(yearStr)
  const monthIndex = Number(monthStr) - 1
  const day = Number(dayStr)
  if (!Number.isFinite(year) || monthIndex < 0 || monthIndex > 11 || day < 1 || day > 31) {
    return null
  }
  return { day, monthIndex, year }
}

/**
 * @param {string} dateStr
 * @param {{ includeYear?: boolean }} [options]
 * @returns {string|null}
 */
function formatDateLabel(dateStr, options = {}) {
  const parts = parseIsoDateParts(dateStr)
  if (!parts) return null
  const month = MONTH_SHORT_ES[parts.monthIndex]
  if (options.includeYear) {
    return `${parts.day} ${month} ${parts.year}`
  }
  return `${parts.day} ${month}`
}

/**
 * @param {unknown} sd
 * @returns {boolean}
 */
function hasValidScheduledFields(sd) {
  if (!sd || typeof sd !== 'object') return false
  if (sd.enabled !== true) return false

  const date = asTrimmedString(sd.date)
  const start = asTrimmedString(sd.start)
  const end = asTrimmedString(sd.end)

  if (!parseIsoDateParts(date)) return false
  if (!TIME_PATTERN.test(start) || !TIME_PATTERN.test(end)) return false

  return true
}

/**
 * Normaliza `scheduledDelivery` a un objeto listo para UI.
 * @param {unknown} sd
 * @returns {{
 *   scheduled: boolean,
 *   date: string|null,
 *   start: string|null,
 *   end: string|null,
 *   labelShort: string,
 *   labelLong: string,
 *   badge: string
 * }}
 */
export function normalizeScheduledDelivery(sd) {
  if (!hasValidScheduledFields(sd)) {
    return {
      scheduled: false,
      date: null,
      start: null,
      end: null,
      labelShort: '—',
      labelLong: 'Lo antes posible',
      badge: 'ASAP',
    }
  }

  const date = asTrimmedString(sd.date)
  const start = asTrimmedString(sd.start)
  const end = asTrimmedString(sd.end)
  const shortDate = formatDateLabel(date, { includeYear: false })
  const longDate = formatDateLabel(date, { includeYear: true })
  const slot = `${start}–${end}`

  return {
    scheduled: true,
    date,
    start,
    end,
    labelShort: `${shortDate} · ${slot}`,
    labelLong: `${longDate} · ${slot}`,
    badge: 'Programado',
  }
}

/**
 * @param {unknown} sd
 * @returns {boolean}
 */
export function isScheduled(sd) {
  return normalizeScheduledDelivery(sd).scheduled
}

/**
 * @param {unknown} sd
 * @returns {string}
 */
export function formatScheduledDeliveryShort(sd) {
  return normalizeScheduledDelivery(sd).labelShort
}

/**
 * @param {unknown} sd
 * @returns {string}
 */
export function formatScheduledDeliveryLong(sd) {
  return normalizeScheduledDelivery(sd).labelLong
}
